import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import {
  NestedQuota,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Constants } from '../constants/quotas';
import { notifyMessage } from '../../shared/constants/notify-message';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';
import { NestedQuotaService } from '../services/nested-quota/nested-quota.service';
import { QuotaPayloadService } from '../services/quotas/quota-payload.service';
import { Subscription } from 'rxjs';
import { UnicornQuotas } from '../../shared/interfaces/unicorn.interface';
import { SurveyUnicorns } from '../domain/survey-unicorns';
import { AidaPayloadService } from '../../shared/services/aida/aida-payload/aida-payload.service';
import { AidaSurveyQuotasEditInspector } from '../../shared/domain/aida-survey-quotas-edit-inspector';
import {
  NestingQualification,
  QuotaGroupingModalData,
} from '../../shared/interfaces/quota-nesting.interface';
import { NestedQualificationBuilder } from '../domain/nested-qualification-builder';
import { CombinedQualification } from '../../shared/interfaces/qualification.interface';
import { NestedQuotaBuilder } from '../domain/nested-quota-builder';
import { SurveyNestedQuotas } from '../domain/survey-nested-quotas';
import { NestedQuotaGroupBuilder } from '../multi-quota-nesting-modal/domain/nested-quota-group-builder';
import { NestedQuotaCombinedKeyBuilder } from '../multi-quota-nesting-modal/domain/nested-quota-combine-key-builder';
import { NestedQuotaGroupSeparator } from '../multi-quota-nesting-modal/domain/nested-quota-group-separator';
@Component({
  selector: 'ps-ui-quota-grouping-modal',
  templateUrl: './quota-grouping-modal.component.html',
  styleUrls: ['./quota-grouping-modal.component.css'],
})
export class QuotaGroupingModalComponent implements OnInit, OnDestroy {
  private readonly _qualifications: Array<CombinedQualification> =
    this._modalData.qualifications;
  private readonly _quotas: Array<QuotaPayload> = this._modalData.quotas;
  private readonly _syncNestedQuotasEmmiter: EventEmitter<Array<NestedQuota>> =
    this._modalData.syncNestedQuotasEmmiter;

  public nestedQuotas: Array<NestedQuota> = this._modalData.nestedQuotas;
  public surveyDetails = this._modalData.surveyDetails;

  private readonly _surveyGoal: number = this._modalData.completes;
  private _subscriptions: Subscription = new Subscription();

  public readonly Constants = Constants;
  public allQualifications: Array<NestingQualification> = [];
  public selectedQualifications: Array<NestingQualification> = [];
  public selectedNestedQuotas: Array<NestedQuota> = [];
  public hasMoreQuotasThanCompletes: boolean = false;
  public hasTableSelectionMode: boolean = false;
  public disableApplyNesting = true;
  public disabledCheckboxesAtLiveSurvey: boolean = false;
  public showDraftSurveyWarningMessage: boolean = false;
  public showLiveSurveyWarningMessage: boolean = false;
  public SURVEY_STATUS = SURVEY_STATUS;
  public totalRemaining: number = 0;
  public isEditModeEnabled: boolean = false;
  public unicornQuotas: UnicornQuotas[] = [];

  constructor(
    @Inject(ModalData)
    private _modalData: QuotaGroupingModalData,
    private _modalRef: ModalRef<
      QuotaGroupingModalComponent,
      {
        quotas: QuotaPayload[];
      }
    >,
    private _toastr: ToasterService,
    private _nestedQuotaService: NestedQuotaService,
    private _quotaPayloadService: QuotaPayloadService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  public ngOnInit(): void {
    this._buildQualificationsForNesting();
    if (this.nestedQuotas.length) {
      this._resetNestedQuotaFlagsOnInitialization();
      this._buildInitialSelectedQualifications();
      this._generateCombinedKeyForNestedQuotas();
    }
    this._checkIfHasMoreQuotasThanCompletes();
    this._assignQuotaGroupRelatedChecks();
    this._updateRemainingField();
    this._handleSubscriptions();
  }

  public get surveyNestedQuotas(): SurveyNestedQuotas {
    return new SurveyNestedQuotas(this.nestedQuotas);
  }

  private _updateRemainingField() {
    const totalAllocation = this.surveyNestedQuotas.allocation();
    this.totalRemaining = this._surveyGoal - totalAllocation;
  }

  private _assignQuotaGroupRelatedChecks() {
    this.disabledCheckboxesAtLiveSurvey =
      this.surveyDetails.ps_survey_status === SURVEY_STATUS.LIVE &&
      this.surveyNestedQuotas.hasGroupedQuotas();
    this.showDraftSurveyWarningMessage =
      this.surveyDetails.ps_survey_status === SURVEY_STATUS.DRAFT &&
      this.surveyNestedQuotas.hasGroupedQuotas();
    this.showLiveSurveyWarningMessage =
      this.surveyDetails.ps_survey_status === SURVEY_STATUS.LIVE &&
      this.surveyNestedQuotas.hasGroupedQuotas();
  }

  private _buildQualificationsForNesting() {
    this.allQualifications = new NestedQualificationBuilder(
      this._qualifications,
      this._quotas,
      this.surveyNestedQuotas.qualificationIds()
    ).build();
  }

  private _buildInitialSelectedQualifications() {
    this.selectedQualifications = this.allQualifications
      .filter((qualification) =>
        this.surveyNestedQuotas
          .qualificationIds()
          .includes(Number(qualification.qualification_code))
      )
      .sort(
        (q1, q2) =>
          this._findNestedIndex(q1.qualification_code) -
          this._findNestedIndex(q2.qualification_code)
      );
  }

  private _findNestedIndex(qualificationId: number): number {
    return this.surveyNestedQuotas
      .qualificationIds()
      .findIndex((qId) => qualificationId === qId);
  }

  private _filterSelections<T extends { isChecked?: boolean }>(
    selections: T[]
  ): T[] {
    return selections.filter((selection) => !!selection.isChecked);
  }

  private _resetNestedQuotaFlagsOnInitialization() {
    this.nestedQuotas = this.nestedQuotas.map((nestedQuota) => ({
      ...nestedQuota,
      ...(nestedQuota.initialQuotaTitle && {
        quota_title: nestedQuota.initialQuotaTitle,
      }),
      ...(nestedQuota.isEdit && { isEdit: false }),
    }));
  }

  public validateQualificationSelectionLimit(e: Event): void {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked && this._hasHitTheNestedQuotaLimits()) {
      e.preventDefault();
      return this._toastr.warning(
        notifyMessage.warningMessage.QUOTA.MAXIMUM_QUOTAS_TO_NEST.replace(
          ':NESTED_QUOTA_LIMIT',
          `${Constants.NESTED_QUOTA_LIMIT}`
        )
      );
    }
  }

  public toggleQualificationSelection(e: Event, index: number) {
    const checked = (e.target as HTMLInputElement).checked;
    this.allQualifications[index].isChecked = checked;
    this.disableApplyNesting = this._isApplyNestingDisabled();
    this._formSelectedQualificationsBasedOnSelection(
      this.allQualifications[index]
    );
    this._rebuildNestedQuotasBasedOnSelections();
    this._assignQuotaGroupRelatedChecks();
    this._resetTableStateAfterQualificationsSelection();
    this._updateRemainingField();
  }

  private _formSelectedQualificationsBasedOnSelection(
    selection: NestingQualification
  ) {
    if (selection.isChecked) {
      this.selectedQualifications.push(selection);
      return;
    }
    this.selectedQualifications = this.selectedQualifications.filter(
      (qualification) =>
        qualification.qualification_code !== selection.qualification_code
    );
  }

  private _isApplyNestingDisabled() {
    return this.allQualifications.every((q) => !q.isChecked);
  }

  private _resetTableStateAfterQualificationsSelection() {
    this.selectedNestedQuotas = [];
    if (!this.selectedQualifications.length) {
      this.hasTableSelectionMode = false;
    }
  }

  private _hasHitTheNestedQuotaLimits(): boolean {
    return this.selectedQualifications.length >= Constants.NESTED_QUOTA_LIMIT;
  }

  private _rebuildNestedQuotasBasedOnSelections(): void {
    if (!this.selectedQualifications.length) {
      this.nestedQuotas = [];
      return;
    }
    this.nestedQuotas = new NestedQuotaBuilder(
      this._quotas,
      this.selectedQualifications,
      this._surveyGoal
    ).build();
    this._checkIfHasMoreQuotasThanCompletes();
  }

  public ungroupNesedQuotaGroup(index: number) {
    const quotaToUngroup = this.nestedQuotas[index];
    const ungrouped = new NestedQuotaGroupSeparator(quotaToUngroup).ungroup();
    this.nestedQuotas.splice(index, 1);
    this.nestedQuotas = [...this.nestedQuotas, ...ungrouped];
    this.disableApplyNesting = false;
    this._assignQuotaGroupRelatedChecks();
  }

  public createGroupNestedQuotas() {
    if (this.selectedNestedQuotas.length <= 1) {
      this._toastr.warning(
        'Error: Please select more than one quota. A minimum of 2 options is required'
      );
      return;
    }
    const filteredNestedQuotas = this.surveyNestedQuotas.filter(
      this.selectedNestedQuotas
    );
    const groupNestedQuota = new NestedQuotaGroupBuilder(
      this.selectedNestedQuotas,
      this.selectedQualifications,
      this.surveyNestedQuotas.titles()
    ).build();
    this.nestedQuotas = [groupNestedQuota, ...filteredNestedQuotas];
    this._assignQuotaGroupRelatedChecks();
    this.hasTableSelectionMode = false;
    this.selectedNestedQuotas = [];
    this.disableApplyNesting = false;
  }

  public toggleNestedQuotaSelection(e: Event, index: number) {
    const selected = (e.target as HTMLInputElement).checked;
    this.nestedQuotas[index].isChecked = selected;
    this.selectedNestedQuotas = this._filterSelections(this.nestedQuotas);
  }

  public clearNesting(): void {
    if (this._showQuotaEditNotAllowedErrorForAida('clear-nesting')) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }
    this.nestedQuotas = [];
    this.selectedQualifications = [];
    this.selectedNestedQuotas = [];
    this.hasTableSelectionMode = false;
    this._assignQuotaGroupRelatedChecks();
    this.allQualifications.forEach((qualification) => {
      qualification.isChecked = false;
    });
    this._sendNestedQuotasToParent();
    this.disableApplyNesting = true;
  }

  private _showQuotaEditNotAllowedErrorForAida(
    scenario: 'apply-nesting' | 'clear-nesting'
  ): boolean {
    if (this.surveyDetails.ps_survey_status === SURVEY_STATUS.DRAFT) {
      return false;
    }
    return new AidaSurveyQuotasEditInspector(
      this.surveyDetails.aida!,
      this.nestedQuotas,
      false,
      scenario
    ).notEditable();
  }

  public applyNesting(): void {
    if (this._showQuotaEditNotAllowedErrorForAida('apply-nesting')) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }
    const hasUniqueTitles = this._hasUniqueItems(
      this.surveyNestedQuotas.titles().filter((title) => !!title)
    );
    if (!hasUniqueTitles) {
      this._toastr.warning('Quota titles should be unique.');
      return;
    }
    this.manageNestedQuotas();
    this._sendNestedQuotasToParent();
    this._modalRef.close();
  }

  private _hasUniqueItems<T>(items: T[]): boolean {
    return [...new Set(items)].length === items.length;
  }

  private _sendNestedQuotasToParent(): void {
    this._syncNestedQuotasEmmiter.emit(this.nestedQuotas);
  }

  private _generateCombinedKeyForNestedQuotas() {
    this.nestedQuotas = this.nestedQuotas.map((nestedQuota) => {
      if (nestedQuota.individual_quotas?.length) {
        return {
          ...nestedQuota,
          isQuotaGrouped: true,
          isChecked: false,
          individual_quotas: nestedQuota.individual_quotas.map((quota) => ({
            ...quota,
            combinedKey: new NestedQuotaCombinedKeyBuilder(
              quota.criteria,
              this.selectedQualifications
            ).build(),
          })),
        };
      }
      return {
        ...nestedQuota,
        combinedKey: new NestedQuotaCombinedKeyBuilder(
          nestedQuota.criteria,
          this.selectedQualifications
        ).build(),
      };
    });
  }

  public manageNestedQuotas() {
    this.nestedQuotas.forEach((nestedQuota) => {
      nestedQuota.initialQuotaTitle = '';
    });
  }

  public updateQuotanestingEdit(nestedQuota: NestedQuota) {
    nestedQuota.initialQuotaTitle = nestedQuota.quota_title;
  }

  public verifyQuotaTitle(nestedQuota: NestedQuota) {
    if (nestedQuota.quota_title === '') {
      nestedQuota.quota_title = nestedQuota.initialQuotaTitle;
    }
  }

  public updateQuotaPercentage(nestedQuota: NestedQuota) {
    this._nestedQuotaService.updateQuotaPercentage(
      nestedQuota,
      this.nestedQuotas,
      this._surveyGoal
    );
    this._nestedQuotaService.updateIndividualQuotas(
      nestedQuota,
      this._surveyGoal
    );
    this._updateRemainingField();
  }

  public updateQuotaAllocation(nestedQuota: NestedQuota) {
    this._nestedQuotaService.updateQuotaAllocation(
      nestedQuota,
      this.nestedQuotas,
      this._surveyGoal
    );
    this._nestedQuotaService.updateIndividualQuotas(
      nestedQuota,
      this._surveyGoal
    );
    this._updateRemainingField();
  }

  public setInitialAllocation(nestedQuota: NestedQuota) {
    if (nestedQuota.required_count && nestedQuota.initialQuotaAllocation) {
      nestedQuota.required_count = nestedQuota.initialQuotaAllocation;
    }
  }

  public disableApplyNestingButton() {
    this.disableApplyNesting = false;
  }
  private _checkIfHasMoreQuotasThanCompletes(): void {
    this.hasMoreQuotasThanCompletes =
      this.nestedQuotas.length > this._surveyGoal;
  }

  private _handleSubscriptions(): void {
    this._handleApplyNestingDisabling();
    this._handleSurveyUnicorns();
  }

  private _handleApplyNestingDisabling(): void {
    this._subscriptions.add(
      this._nestedQuotaService.isNestedQuotasValid.subscribe((isValid) => {
        this.disableApplyNesting = !isValid;
      })
    );
  }

  private _handleSurveyUnicorns(): void {
    this._subscriptions.add(
      this._quotaPayloadService.getSurveyUnicorns().subscribe((response) => {
        this.unicornQuotas = response;
      })
    );
  }

  public shouldShowToolTip(buyerQuotaId: string = '') {
    return new SurveyUnicorns(this.unicornQuotas).hasQuota(buyerQuotaId);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
